/**
 * Zones of control (ADR-0050).
 *
 * Every enemy soldier on land claims the tiles around it. A land unit may not step
 * from one claimed tile straight to another — it has to stop, go round, or fight —
 * unless it is stepping into its own city or onto its own units. That is what lets a
 * line of defenders actually hold a line, instead of being walked through.
 *
 * Only civilisations at war exert zones on each other, and only units this one can
 * see: a zone is a thing you notice by running into it. Scouts and explorers ignore
 * them (`ignore_zoc`), and so does anything landing from the sea.
 */

import type { Content, UnitDef } from '../../../content/schema/index.js';
import { cityAt } from '../cities/index.js';
import { canAttack, canDefend } from '../combat/index.js';
import { atWar } from '../diplomacy/index.js';
import { neighborsByDirection, tileCount } from '../state/grid.js';
import { NONE, VIS_VISIBLE, type GameState, type Id } from '../state/types.js';
import { allUnits, unitDef, unitIndex } from './index.js';

/** Tiles inside an enemy zone of control, for this civilisation: 1 inside, 0 outside. */
export function zocPlane(state: GameState, content: Content, civId: Id): Uint8Array {
  const plane = new Uint8Array(tileCount(state.map));
  const vision = state.vision[civId];
  const scratch = new Int32Array(6);
  for (const unit of allUnits(state)) {
    if (unit.owner === civId || unit.aboard !== NONE) continue;
    const def = unitDef(content, unit);
    if (def.domain !== 'land' || !(canAttack(def) || canDefend(def))) continue;
    if (!atWar(state, civId, unit.owner)) continue;
    const at = unitIndex(state.map, unit);
    if (vision && vision[at] !== VIS_VISIBLE) continue;
    neighborsByDirection(state.map, at, scratch);
    for (let d = 0; d < 6; d++) if (scratch[d]! !== -1) plane[scratch[d]!] = 1;
  }
  return plane;
}

/** Whether a unit of this type is bound by zones of control at all. */
export function feelsZoc(def: UnitDef): boolean {
  return def.domain === 'land' && !def.abilities.includes('ignore_zoc');
}

/**
 * Whether a step from one tile to the next is forbidden by zones of control. Moving
 * into your own city or onto your own units is always allowed — that is falling back,
 * not slipping past.
 */
export function zocBlocks(
  state: GameState,
  content: Content,
  civId: Id,
  plane: Uint8Array,
  from: number,
  to: number,
): boolean {
  if (plane[from] !== 1 || plane[to] !== 1) return false;
  if (content.terrain.get(state.map.terrain[from]!).water) return false;
  const city = cityAt(state, state.map, to);
  if (city && city.owner === civId) return false;
  return !allUnits(state).some((u) => u.owner === civId && unitIndex(state.map, u) === to);
}
